<template>
  <div class="clearfix mt-4 pb-5 mx-auto px-3 ibox">
    <div class="clearfix row pb-2 pt-3 mb-2">
      <div class="col-sm-6 px-3 company-form-box-title">ID Card Templates (New)</div>
    </div>    

    <div class="table-responsive">
      <table class="table dataTable">
        <thead>
          <tr class="table-head">
            <td class="pl-4" style="width: 50px">#</td>
            <td>Template Name</td>
            <td class="text-center">Layout</td>
            <td class="text-center">Orientation</td>
            <td class="text-right pr-4">Actions</td>
          </tr>
        </thead>
        <tbody>
          <tr v-if="templates.length === 0">
            <td colspan="5" class="text-center text-muted py-4">No templates found.</td>
          </tr>
          <tr v-for="(template, index) in templates" :key="template.id" class="hover-bg table-row border-bottom mx-2">
            <td class="list-columns pl-4 align-middle">{{ index + 1 }}</td>
            <td class="list-columns align-middle">
              <span>{{ template.template_name || "Untitled" }}</span>
              <span v-if="template.is_default"> (default)</span>
              <b-icon icon="check-circle-fill" variant="success" class="ml-2" aria-hidden="true" title="Active" v-if="template.is_active"></b-icon>
            </td>
            <td class="text-center align-middle">
              <b-badge variant="light" class="border text-dark">Layout {{ template.template_key }}</b-badge>
            </td>
            <td class="text-center align-middle">
              {{ template.card_orientation ? 'Vertical' : 'Horizontal' }}
            </td>
            <!-- Action dropdown mirrors the HR Admin Portal one (resources/js/components/AllTemplates.vue):
                 a position-relative wrapper the globally-positioned .action-dropdown-menu anchors to, and
                 outline-secondary buttons inside a b-button-group. The variant matters — .btn-secondary is
                 caught by `.admin-portal .dropdown-menu.action-dropdown-menu .btn.btn-secondary`, which
                 right-aligns the labels instead of leaving them on the global left-aligned .btn rule. -->
            <td class="clearfix text-right pr-4 align-middle">
              <div class="float-right position-relative">
                <b-button-toolbar>
                  <a :id="'actionDropdown-' + template.id" class="dropdown-toggle clearfix action-dropdown-toggle"
                     style="font-size: 16px" href="#" role="button" data-toggle="dropdown"
                     aria-haspopup="true" aria-expanded="false">
                    <b-icon icon="three-dots-vertical"></b-icon>
                  </a>
                  <div class="dropdown-menu action-dropdown-menu"
                       :aria-labelledby="'actionDropdown-' + template.id" style="width: 230px">
                    <b-button-group class="mr-1">

                      <!-- Make Active — offered on the default template too, so a
                           company can always fall back to it after activating
                           one of its own. -->
                      <b-button v-if="!template.is_active" variant="outline-secondary" title="Make This Template Active"
                                @click="makeActive(template)">
                        <b-icon icon="arrow-right-circle" class="mr-2" aria-hidden="true"></b-icon>Make Active
                      </b-button>
                      <b-button v-else variant="outline-secondary" title="This Template is Active">
                        <b-icon icon="check-circle-fill" class="mr-2" variant="success" aria-hidden="true"></b-icon>Active Template
                      </b-button>

                      <!-- Duplicate — offered on every template, a copy included:
                           duplicating is how a company branches an existing
                           design without disturbing the one it came from, and
                           that is as useful on its own cards as on the default.
                           The copy's name is numbered server-side. -->
                      <b-button variant="outline-secondary" title="Duplicate Template Details" @click="duplicateTemplate(template.id)">
                        <b-icon icon="clipboard-plus" class="mr-2" aria-hidden="true"></b-icon>Duplicate
                      </b-button>

                      <!-- Select Signatory — offered on every template. On the
                           default it is the one setting a company can change
                           without duplicating; on its own templates it is a
                           shortcut to the same field in the editor, for
                           swapping the signature without opening the full
                           design form. -->
                      <b-button variant="outline-secondary" title="Add/Edit Signatory" @click="openSignatoryModal(template)">
                        <b-icon icon="clipboard-plus" class="mr-2" aria-hidden="true"></b-icon>Select Signatory
                      </b-button>

                      <!-- Preview (Always enabled, including default) -->
                      <b-button variant="outline-secondary" title="Preview Details" :disabled="previewRowLoadingId === template.id" @click="previewSavedTemplate(template)">
                        <b-spinner small v-if="previewRowLoadingId === template.id" class="mr-2"></b-spinner>
                        <i v-else class="fas fa-list mr-2"></i>Preview Template
                      </b-button>

                      <!-- Download (Always enabled, including default) -->
                      <b-button variant="outline-secondary" title="Download Sample Details" :disabled="downloadRowLoadingId === template.id" @click="downloadTemplate(template)">
                        <b-spinner small v-if="downloadRowLoadingId === template.id" class="mr-2"></b-spinner>
                        <b-icon v-else icon="cloud-download" class="mr-2" aria-hidden="true"></b-icon>Download Template
                      </b-button>

                      <!-- Edit / Delete — not offered at all on a default
                           template: it can be neither edited nor deleted, so a
                           disabled entry was only listing what cannot be done. -->
                      <!-- <b-button v-if="!template.is_default && $acl['id-card/update'] > 0" variant="outline-secondary" -->
                      <b-button v-if="!template.is_default" variant="outline-secondary" title="Edit Template Details"
                                @click="openEdit(template)">
                        <b-icon icon="pencil" class="mr-2" aria-hidden="true"></b-icon>Edit
                      </b-button>

                      <!-- Delete is also withheld from the active template: it
                           is the design every card is generated from, so
                           removing it would drop the company back onto the
                           shared default without saying so. Making another
                           template active first is what releases it. -->
                      <!-- <b-button v-if="!template.is_default && !template.is_active && $acl['id-card/delete'] > 0" variant="outline-secondary"  -->
                      <b-button v-if="!template.is_default && !template.is_active" variant="outline-secondary" title="Delete Template Details"
                                @click="deleteTemplate(template.id)">
                        <b-icon icon="trash" class="mr-2" aria-hidden="true"></b-icon>Delete
                      </b-button>

                    </b-button-group>
                  </div>
                </b-button-toolbar>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ── Edit Modal ─────────────────────────────────────────────────── -->
    <b-modal
      v-model="showModal"
      :title="editModalTitle"
      size="xl"
      scrollable
      no-close-on-backdrop
      :dialog-class="editorExpanded ? 'idcard-modal-fullscreen' : ''"
      footer-class="idcard-editor-footer"
      @hide="onModalHide"
    >
      <!-- Name & Key -->
      <b-row>
        <b-col md="4">
          <b-form-group
            label="Template Name *"
            :invalid-feedback="'Template Name is required'"
            :state="nameState"
          >
            <b-form-input
              v-model="modalSettings.template_name"
              placeholder="e.g. Employee Card 2026"
              required
              :state="nameState"
            />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="Layout Type">
            <b-form-select
              v-model="modalSettings.template_key"
              :options="templateDesignOptions"
              @change="onTemplateDesignChange"
            ></b-form-select>
            <small class="text-muted">Switching this replaces the design below with that layout's defaults.</small>
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group
            label="Authorized Signature *"
            :invalid-feedback="'Authorized Signature is required'"
            :state="signatoryState"
          >
            <b-form-select
              v-model="modalSettings.signatory_id"
              :options="signatoryOptions"
              required
              :state="signatoryState"
              @change="onSignatoryChange"
            ></b-form-select>
            <div v-if="owner_sig_preview" class="mt-2 text-center" style="background:#fff; border:1px solid #ccc; padding:5px; border-radius:4px; max-width: 150px;">
              <b-img :src="owner_sig_preview" height="30" alt="Signature preview" style="max-width:100%; object-fit:contain;" />
            </div>
          </b-form-group>
        </b-col>
      </b-row>

      <hr />

      <!-- Settings Row -->
      <b-row>
        <b-col cols="12" md="3">
          <b-form-group
            label="Font Style *"
            :invalid-feedback="'Font Style is required'"
            :state="fontStyleState"
          >
            <b-form-select
              v-model="modalSettings.font_style"
              :options="idCardFontOptions"
              required
              :state="fontStyleState"
            />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="3">
          <b-form-group
            label="Card Orientation *"
            :invalid-feedback="'Card Orientation is required'"
            :state="orientationState"
          >
            <b-form-select
              v-model="modalSettings.card_orientation"
              :options="idCardOrientationOptions"
              required
              :state="orientationState"
            />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="3">
          <b-form-group
            label="Image Shape *"
            :invalid-feedback="'Image Shape is required'"
            :state="imageShapeState"
          >
            <b-form-select
              v-model="modalSettings.image_shape"
              :options="idCardPhotoShapeOptions"
              required
              :state="imageShapeState"
            />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="3">
          <b-form-group
            label="Footer Text (Back) *"
            :invalid-feedback="footerTextFeedback"
            :state="footerTextState"
          >
            <b-form-textarea
              v-model="modalSettings.footer_text"
              rows="2"
              max-rows="4"
              maxlength="90"
              required
              :state="footerTextState"
            />
            <small class="text-muted" :class="{'text-danger': modalSettings.footer_text && modalSettings.footer_text.length >= 90}">
              Characters: {{ modalSettings.footer_text ? modalSettings.footer_text.length : 0 }}/90
            </small>
          </b-form-group>
        </b-col>
      </b-row>

      <hr />

      <!-- Color Settings — same swatch-and-code control the Edit Leave
           Category form uses (app/Modules/Roster/Components/LeaveTypes.vue):
           the field's name with its current hex below it, and a full-height
           colour block that opens the picker. -->
      <p class="font-weight-bold mb-2">Color Settings *</p>
      <b-row>
        <b-col
          cols="6"
          md="4"
          v-for="field in primaryColorFields"
          :key="field.key"
        >
          <b-form-group
            :invalid-feedback="field.label + ' is required'"
            :state="colorFieldState(field.key)"
          >
            <div class="d-flex align-items-center justify-content-between ibox border rounded mb-0 row idcard-color-box">
              <div class="col-md-6 pr-0">
                <label :for="'color-' + field.key" class="px-2 py-0 text-dark mb-0 font-weight-bold text-nowrap">
                  {{ field.label }} *
                  <span class="d-block text-secondary font-weight-normal monospace-font">{{ modalSettings[field.key] }}</span>
                </label>
              </div>
              <div class="col-md-6 px-0">
                <b-form-input
                  :id="'color-' + field.key"
                  type="color"
                  v-model="modalSettings[field.key]"
                  class="border-0 p-0 idcard-color-swatch"
                  required
                />
              </div>
            </div>
          </b-form-group>
        </b-col>
      </b-row>

      <div class="d-flex justify-content-end mb-2">
        <b-form-checkbox switch size="lg" v-model="showAdvanced">
          Advanced Settings
        </b-form-checkbox>
      </div>

      <template v-if="showAdvanced">
        <p class="font-weight-bold mb-2">Advance Color</p>
        <b-row>
          <b-col
            cols="6"
            md="4"
            v-for="field in advancedColorFields"
            :key="field.key"
          >
            <b-form-group>
              <div class="d-flex align-items-center justify-content-between ibox border rounded mb-0 row idcard-color-box">
                <div class="col-md-6 pr-0">
                  <label :for="'color-' + field.key" class="px-2 py-0 text-dark mb-0 font-weight-bold text-nowrap">
                    {{ field.label }}
                    <span class="d-block text-secondary font-weight-normal monospace-font">{{ modalSettings[field.key] }}</span>
                  </label>
                </div>
                <div class="col-md-6 px-0">
                  <b-form-input
                    :id="'color-' + field.key"
                    type="color"
                    v-model="modalSettings[field.key]"
                    class="border-0 p-0 idcard-color-swatch"
                  />
                </div>
              </div>
            </b-form-group>
          </b-col>
        </b-row>

        <hr />

        <!-- Item Show Settings -->
        <b-card no-body class="mb-3 mt-4 shadow-sm" v-if="modalSettings.item_show">
        <template #header>
          <div class="font-weight-bold d-flex align-items-center" style="font-size: 15px;">
            <b-icon icon="card-checklist" class="mr-2"></b-icon> Item Show Settings
          </div>
        </template>
        <!-- Only the items the chosen layout + orientation actually draws are
             listed. A toggle for something the design never renders (Company
             Name Back, which no layout has; Company Logo Back on Layout 1
             Horizontal) reads as a setting that silently does nothing — see
             supportsItem(). -->
        <div class="row m-0">
          <div class="col-md-6 p-0 border-right">
            <div class="list-group list-group-flush h-100 p-2">
              <field-style-box
                v-if="supportsItem('company_name_front')"
                label="Company Name Front"
                :style-obj="modalSettings.item_styles.company_name_front"
                :show.sync="modalSettings.item_show.company_name_front"
                :font-options="fieldFontOptions"
              ></field-style-box>
              <div v-if="supportsItem('company_logo_front')" class="list-group-item d-flex justify-content-between align-items-center py-3">
                <span>Company Logo Front</span>
                <b-form-checkbox switch size="lg" v-model="modalSettings.item_show.company_logo_front"></b-form-checkbox>
              </div>
              <div v-if="supportsItem('employee_photo_ring')" class="list-group-item d-flex justify-content-between align-items-center py-3">
                <span>Employee Photo Border</span>
                <b-form-checkbox switch size="lg" v-model="modalSettings.item_show.employee_photo_ring"></b-form-checkbox>
              </div>
              <field-style-box
                v-if="supportsItem('job_position')"
                label="Job Position"
                :style-obj="modalSettings.item_styles.job_position"
                :show.sync="modalSettings.item_show.job_position"
                :font-options="fieldFontOptions"
              ></field-style-box>
              <!-- Employee ID / Department / Blood Group: drawn by every
                   design, so there is nothing to filter here — and filtering
                   would desync draggable's indexes from item_order anyway. -->
              <draggable v-model="modalSettings.item_order" handle=".drag-handle" tag="div">
                <field-style-box
                  v-for="key in modalSettings.item_order"
                  :key="key"
                  :label="itemOrderLabels[key]"
                  :style-obj="modalSettings.item_styles[key]"
                  :show.sync="modalSettings.item_show[key]"
                  :font-options="fieldFontOptions"
                  handle
                ></field-style-box>
              </draggable>
              <field-style-box
                v-if="supportsItem('phone')"
                label="Phone"
                :style-obj="modalSettings.item_styles.phone"
                :show.sync="modalSettings.item_show.phone"
                :font-options="fieldFontOptions"
              ></field-style-box>
            </div>
          </div>
          <div class="col-md-6 p-0">
            <div class="list-group list-group-flush h-100 p-2">
              <field-style-box
                v-if="supportsItem('company_address')"
                label="Company Address"
                :style-obj="modalSettings.item_styles.company_address"
                :show.sync="modalSettings.item_show.company_address"
                :font-options="fieldFontOptions"
              ></field-style-box>
              <field-style-box
                v-if="supportsItem('company_email')"
                label="Company Email"
                :style-obj="modalSettings.item_styles.company_email"
                :show.sync="modalSettings.item_show.company_email"
                :font-options="fieldFontOptions"
              ></field-style-box>
              <field-style-box
                v-if="supportsItem('company_phone')"
                label="Company Phone"
                :style-obj="modalSettings.item_styles.company_phone"
                :show.sync="modalSettings.item_show.company_phone"
                :font-options="fieldFontOptions"
              ></field-style-box>
              <field-style-box
                v-if="supportsItem('company_website')"
                label="Company Website"
                :style-obj="modalSettings.item_styles.company_website"
                :show.sync="modalSettings.item_show.company_website"
                :font-options="fieldFontOptions"
              ></field-style-box>
              <div v-if="supportsItem('company_signature')" class="list-group-item d-flex justify-content-between align-items-center py-3">
                <span>Authorized Signature</span>
                <b-form-checkbox switch size="lg" v-model="modalSettings.item_show.company_signature"></b-form-checkbox>
              </div>
              <div v-if="supportsItem('company_logo_back')" class="list-group-item d-flex justify-content-between align-items-center py-3">
                <span>Company Logo Back</span>
                <b-form-checkbox switch size="lg" v-model="modalSettings.item_show.company_logo_back"></b-form-checkbox>
              </div>
            </div>
          </div>
        </div>
      </b-card>
      </template>

      <template #modal-footer>
        <b-button variant="primary" class="mr-auto" :disabled="previewLoading" @click="previewTemplate">
          <b-spinner small v-if="previewLoading" class="mr-1"></b-spinner>
          <b-icon v-else icon="eye" class="mr-1"></b-icon> Preview PDF
        </b-button>
        <b-button variant="secondary" class="idcard-cancel-btn" @click="showModal = false">Cancel</b-button>
        <!-- <b-button v-if="$acl['id-card/update'] > 0" variant="primary" :disabled="modalSaving" @click="saveModal"> -->
        <b-button variant="primary" class="idcard-update-btn" :disabled="modalSaving" @click="saveModal">
          Update Template
        </b-button>
      </template>
    </b-modal>

    <!-- ── PDF Preview Modal ──────────────────────────────────────────── -->
    <b-modal
      v-model="showPreviewModal"
      title="Template Preview"
      size="lg"
      hide-footer
      @hidden="onPreviewModalHidden"
    >
      <template-preview
        v-if="previewFaces.length"
        :faces="previewFaces"
      ></template-preview>
    </b-modal>

    <!-- ── Saved Template Preview Modal (list row action) ─────────────── -->
    <b-modal
      v-model="showSavedPreviewModal"
      title="Template Preview"
      size="lg"
      hide-footer
      @hidden="onSavedPreviewModalHidden"
    >
      <template-preview
        v-if="savedPreviewFaces.length"
        :faces="savedPreviewFaces"
      ></template-preview>
    </b-modal>

    <!-- ── Select Signatory Modal (Default template) ─────────────────── -->
    <b-modal
      v-model="showSignatoryModal"
      title="Select Signatory"
      size="sm"
      no-close-on-backdrop
      modal-class="idcard-confirm-modal"
    >
      <b-form-group
        label="Authorized Signature *"
        :invalid-feedback="'Authorized Signature is required'"
        :state="signatoryModalState"
      >
        <b-form-select
          v-model="signatoryModalValue"
          :options="signatoryOptions"
          :state="signatoryModalState"
          @change="onSignatoryModalChange"
        ></b-form-select>
        <div v-if="signatoryModalPreview" class="mt-2 text-center" style="background:#fff; border:1px solid #ccc; padding:5px; border-radius:4px; max-width: 150px;">
          <b-img :src="signatoryModalPreview" height="30" alt="Signature preview" style="max-width:100%; object-fit:contain;" />
        </div>
      </b-form-group>
      <template #modal-footer>
        <b-button variant="secondary" @click="showSignatoryModal = false">Cancel</b-button>
        <b-button variant="primary" :disabled="signatoryModalSaving" @click="saveSignatory">Save</b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import TemplatePreview from './TemplatePreview.vue';

const TEXT_FIELD_STYLE_DEFAULTS = {
  company_name_front: { font_size: 14, color: "#111111" },
  job_position: { font_size: 12, color: "#444444" },
  employee_id: { font_size: 10, color: "#111111" },
  department: { font_size: 10, color: "#111111" },
  blood_group: { font_size: 10, color: "#111111" },
  phone: { font_size: 10, color: "#111111" },
  company_address: { font_size: 9, color: "#4a4a4a" },
  company_email: { font_size: 9, color: "#4a4a4a" },
  company_phone: { font_size: 9, color: "#4a4a4a" },
  company_website: { font_size: 9, color: "#4a4a4a" },
  company_name_back: { font_size: 14, color: "#2DAE8C" }
};

function createDefaultItemStyles() {
  const styles = {};
  Object.keys(TEXT_FIELD_STYLE_DEFAULTS).forEach(key => {
    styles[key] = { enabled: false, font_family: "", ...TEXT_FIELD_STYLE_DEFAULTS[key] };
  });
  return styles;
}

const DEFAULT_ITEM_SHOW = {
  company_name_front: true,
  company_logo_front: true,
  employee_photo_ring: true,
  job_position: true,
  employee_id: true,
  department: true,
  blood_group: true,
  phone: true,
  company_address: true,
  company_email: true,
  company_phone: true,
  company_website: true,
  company_signature: true,
  company_logo_back: true,
  company_name_back: true
};

const DEFAULT_ITEM_ORDER = ["employee_id", "department", "blood_group"];

// Item Show entries every card design draws, whichever layout and orientation
// is selected.
const COMMON_LAYOUT_ITEMS = [
  "company_name_front",
  "company_logo_front",
  "employee_photo_ring",
  "job_position",
  "employee_id",
  "department",
  "blood_group",
  "phone",
  "company_address",
  "company_email",
  "company_phone",
  "company_website"
];

// What each design draws *on top of* the common set, keyed by
// `${template_key}-${orientation}` and taken from the Blade views in
// app/Modules/IdCard/Views. Two settings appear in no list at all and are
// therefore never offered:
//   - company_name_back  : no layout prints the company name on the back face
//   - company_signature  : no layout prints the signature image
// Layout 1 Horizontal is the one design with no back-face logo either.
// Keep this in step with the views — a toggle listed here that the view does
// not honour is a setting that silently does nothing.
const LAYOUT_EXTRA_ITEMS = {
  "1-horizontal": [],
  "1-vertical": ["company_logo_back", "company_signature"],
  "2-horizontal": ["company_logo_back"],
  "2-vertical": ["company_logo_back", "company_signature"],
  "3-horizontal": ["company_logo_back"],
  "3-vertical": ["company_logo_back", "company_signature"]
};

const FieldStyleBox = {
  name: 'FieldStyleBox',
  props: {
    label: { type: String, required: true },
    styleObj: { type: Object, required: true },
    show: { type: Boolean, default: true },
    fontOptions: { type: Array, required: true },
    handle: { type: Boolean, default: false }
  },
  methods: {
    clampFontSize() {
      let size = parseFloat(this.styleObj.font_size);
      if (isNaN(size)) size = 8;
      size = Math.min(35, Math.max(8, size));
      this.styleObj.font_size = size;
    }
  },
  template: `
    <div class="field-style-box list-group-item p-0 mb-1 border rounded overflow-hidden">
      <div class="d-flex justify-content-between align-items-center py-2 px-3">
        <span>
          <b-icon v-if="handle" icon="grip-vertical" class="drag-handle mr-2 text-muted" style="cursor: grab;"></b-icon>
          {{ label }}
        </span>
        <div class="d-flex align-items-center">
          <b-form-checkbox size="sm" class="mr-3 mb-0" v-model="styleObj.enabled">
            <small class="text-muted">Customize</small>
          </b-form-checkbox>
          <b-form-checkbox switch size="lg" :checked="show" @change="$emit('update:show', $event)"></b-form-checkbox>
        </div>
      </div>
      <b-collapse :visible="styleObj.enabled">
        <div class="px-3 pb-3 pt-2 border-top bg-light">
          <b-row>
            <b-col cols="12" md="5">
              <b-form-group class="mt-3" label="Font Style" label-class="small mb-1 text-muted">
                <b-form-select v-model="styleObj.font_family" :options="fontOptions" size="sm" />
              </b-form-group>
            </b-col>
            <b-col cols="6" md="3">
              <b-form-group label="Font Size (8-35px)" label-class="small mb-1 text-muted">
                <b-form-input type="number" min="8" max="35" v-model.number="styleObj.font_size" size="sm" @change="clampFontSize" />
              </b-form-group>
            </b-col>
            <b-col cols="6" md="4">
              <b-form-group class="mt-3" label="Color" label-class="small mb-1 text-muted">
                <div class="d-flex align-items-center" style="gap: 6px">
                  <input type="color" v-model="styleObj.color" class="color-picker-input" />
                  <b-form-input v-model="styleObj.color" size="sm" class="monospace-font" />
                </div>
              </b-form-group>
            </b-col>
          </b-row>
        </div>
      </b-collapse>
    </div>
  `
};

export default {
  components: { draggable, FieldStyleBox, TemplatePreview },
  data() {
    return {
      loading: false,
      templates: [],
      signatories: [],
      owner_sig_preview: null,

      // Modal State
      showModal: false,
      modalSaving: false,
      modalSubmitted: false,
      showAdvanced: false,
      editorExpanded: false,
      showPreview: false,
      editorKey: 0,
      editingTemplate: null,
      previousTemplateKey: "",
      // Serialized settings as the edit modal was opened, so save can tell
      // whether anything actually changed. Null while no modal is open.
      openSnapshot: null,
      // Lets a close through the unsaved-changes guard in onModalHide() — set
      // by a successful save and by confirming the discard prompt.
      allowModalClose: false,
      // Cache of company_id=0 defaults per template_key, keyed by String(key),
      // so switching between the same couple of layouts within a session
      // doesn't re-fetch the same defaults over and over.
      layoutDefaultsCache: {},

      // Preview State — one standalone HTML document per card face, built
      // server-side from the same markup the PDF is rendered from.
      showPreviewModal: false,
      previewLoading: false,
      previewFaces: [],

      // Saved Template Preview Modal State (list row action)
      showSavedPreviewModal: false,
      savedPreviewFaces: [],
      previewRowLoadingId: null,

      // Download State (list row action)
      downloadRowLoadingId: null,

      // Select Signatory Modal State (default template)
      showSignatoryModal: false,
      signatoryModalSaving: false,
      signatoryModalSubmitted: false,
      signatoryModalTemplate: null,
      signatoryModalValue: null,
      signatoryModalPreview: null,

      templateDesignOptions: [
        { value: "1", text: "Layout 1" },
        { value: "2", text: "Layout 2" },
        { value: "3", text: "Layout 3" }
      ],

      modalSettings: {
        template_name: "",
        template_key: "",
        font_style: "Arial",
        card_orientation: true,
        image_shape: true,
        footer_text: "",
        primary_color: "#8B0000",
        secondary_color: "#000000",
        name_text_color: "#111111",
        job_title_color: "#444444",
        department_color: "#111111",
        info_text_color: "#111111",
        icon_color: "#8B0000",
        back_text_color: "#ffffff",
        signatory_id: null,
        custom_html: "",
        item_order: ["employee_id", "department", "blood_group"],
        item_styles: createDefaultItemStyles()
      },

      idCardEditorConfig: {
        allowedContent: true,
        height: 400,
        toolbar: [
          { name: "document", items: ["Source"] },
          { name: "clipboard", items: ["Undo", "Redo"] },
          { name: "basicstyles", items: ["Bold", "Italic", "Underline"] },
          { name: "colors", items: ["TextColor", "BGColor"] },
          { name: "tools", items: ["Maximize"] }
        ]
      },

      idCardOrientationOptions: [
        { value: true, text: "Vertical" },
        { value: false, text: "Horizontal" }
      ],
      idCardPhotoShapeOptions: [
        { value: true, text: "Round (Circle)" },
        { value: false, text: "Square" }
      ],
      idCardFontOptions: [
        { value: "Poppins", text: "Poppins" },
        { value: "Barlow", text: "Barlow" },
        { value: "Times New Roman", text: "Times New Roman" },
        { value: "Roboto", text: "Roboto" },
        { value: "Google Sans Flex", text: "Google Sans Flex" },
        { value: "Mozilla Text", text: "Mozilla Text" },
        { value: "IBM Plex Serif", text: "IBM Plex Serif" },
        { value: "Open Sans", text: "Open Sans" },
        { value: "Playfair Display", text: "Playfair Display" },
        { value: "Inter", text: "Inter" },
        { value: "Montserrat", text: "Montserrat" },
        { value: "Lato", text: "Lato" },
        { value: "Nunito Sans", text: "Nunito Sans" },
        { value: "Raleway", text: "Raleway" },
      ],
      primaryColorFields: [
        { key: "primary_color", label: "Primary" },
        { key: "secondary_color", label: "Secondary" }
      ],
      // "Icon Color" is deliberately absent: it only ever tinted the
      // location/email/phone SVGs built by
      // IdCardTemplateController::getIcon(), and no card design draws those
      // icons — so the picker could not change anything on any layout, in any
      // orientation. The icon_color column and getIcon() are left in place for
      // whenever a design does start drawing them.
      advancedColorFields: [
        { key: "name_text_color", label: "Name Text" },
        { key: "job_title_color", label: "Job Title" },
        { key: "department_color", label: "Dept Text" },
        { key: "info_text_color", label: "Info Text" },
        { key: "back_text_color", label: "Back Text" }
      ],
      itemOrderLabels: {
        employee_id: "Employee ID",
        department: "Department",
        blood_group: "Blood Group"
      }
    }
  },
  computed: {
    // "Edit ID Card Template (Landscape)" — which template is being edited has
    // to be readable from the modal itself; "Edit Template" alone left no way
    // to tell, and did not say what kind of template either.
    editModalTitle() {
      const name = (this.modalSettings.template_name || '').trim();
      return name ? 'Edit ID Card Template (' + name + ')' : 'Edit ID Card Template';
    },
    // Item Show keys the currently selected layout + orientation renders.
    supportedItems() {
      const key = String(this.modalSettings.template_key || '')
        + '-' + (this.modalSettings.card_orientation ? 'vertical' : 'horizontal');
      return COMMON_LAYOUT_ITEMS.concat(LAYOUT_EXTRA_ITEMS[key] || []);
    },
    signatoryOptions() {
      var options = [{ value: null, text: 'Select Signatory' }];
      this.signatories.forEach(function(s) {
        options.push({ value: s.id, text: s.name + ' — ' + (s.designation || '') });
      });
      return options;
    },
    fieldFontOptions() {
      return [{ value: "", text: "Template Default" }, ...this.idCardFontOptions];
    },
    nameState() {
      if (!this.modalSubmitted) return null;
      return !!(this.modalSettings.template_name && this.modalSettings.template_name.trim().length);
    },
    signatoryState() {
      if (!this.modalSubmitted) return null;
      return this.modalSettings.signatory_id !== null && this.modalSettings.signatory_id !== undefined && this.modalSettings.signatory_id !== '';
    },
    fontStyleState() {
      if (!this.modalSubmitted) return null;
      return !!(this.modalSettings.font_style && this.modalSettings.font_style.trim().length);
    },
    orientationState() {
      if (!this.modalSubmitted) return null;
      return this.modalSettings.card_orientation !== null && this.modalSettings.card_orientation !== undefined;
    },
    imageShapeState() {
      if (!this.modalSubmitted) return null;
      return this.modalSettings.image_shape !== null && this.modalSettings.image_shape !== undefined;
    },
    footerTextState() {
      if (!this.modalSubmitted) return null;
      const text = this.modalSettings.footer_text;
      return !!(text && text.trim().length) && text.length <= 90;
    },
    signatoryModalState() {
      if (!this.signatoryModalSubmitted) return null;
      return this.signatoryModalValue !== null && this.signatoryModalValue !== undefined && this.signatoryModalValue !== '';
    },
    footerTextFeedback() {
      const text = this.modalSettings.footer_text;
      if (!text || !text.trim().length) return 'Footer Text is required';
      if (text.length > 90) return 'Maximum 90 characters allowed';
      return '';
    }
  },
  mounted() {
    this.fetchTemplates();
    this.fetchSignatories();
  },
  methods: {
    supportsItem(key) {
      return this.supportedItems.indexOf(key) !== -1;
    },
    // Signatures are served through an authenticated, company-scoped endpoint
    // rather than linked straight at /images/company/signature/<file>: that
    // path is public, so anyone holding a filename could fetch another
    // company's signature image without being logged in at all.
    signatureUrl(signatoryId) {
      return '/id-card/signatory-signature/' + signatoryId;
    },
    fetchSignatories() {
      axios
        .get("/id-card/signatories")
        .then((response) => {
          this.signatories = response.data || [];
        })
        .catch((error) => {
          console.error("Error loading signatories:", error);
        });
    },
    onSignatoryChange(sigId) {
      if (!sigId) {
        this.owner_sig_preview = null;
        return;
      }
      var found = null;
      for (var i = 0; i < this.signatories.length; i++) {
        if (this.signatories[i].id == sigId) {
          found = this.signatories[i];
          break;
        }
      }
      if (found && found.signature) {
        this.owner_sig_preview = this.signatureUrl(found.id);
      } else {
        this.owner_sig_preview = null;
      }
    },
    // `silent` skips the list overlay, for the refresh that follows an action
    // the HR Admin Portal performs without one (Make Active, Duplicate).
    fetchTemplates(silent) {
      if (!silent) this.loading = true;
      axios.get('/id-card/index')
        .then(response => {
          this.templates = response.data;
        })
        .catch(error => {
          console.error("Error fetching templates:", error);
          this.showError("Failed to load templates.");
        })
        .finally(() => {
          if (!silent) this.loading = false;
        });
    },
    makeActive(template) {
      const message = template.is_default
        ? 'Are you sure you want to switch back to the default template? Your currently active template will be deactivated.'
        : 'Are you sure you want to make this template active? It will be used for all ID cards from now on.';
      this.$bvModal.msgBoxConfirm(message, {
        title: 'Please Confirm',
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'primary',
        okTitle: 'Yes',
        cancelVariant: 'secondary',
        cancelTitle: 'Cancel',
        footerClass: 'p-2',
        modalClass: 'idcard-confirm-modal',
        hideHeaderClose: false,
        centered: true
      }).then(confirmed => {
        if (!confirmed) return;
        // No list overlay here — HR Admin Portal's make_default() doesn't show one either.
        axios.post('/id-card/make-default', { id: template.id })
          .then(response => {
            if (response.data.status) {
              this.showSuccess(response.data.message);
              this.fetchTemplates(true);
            } else {
              this.showError(response.data.message);
            }
          });
      });
    },
    // No list overlay here — HR Admin Portal's duplicate_template() doesn't show one either.
    duplicateTemplate(id) {
      axios.post('/id-card/duplicate', { id })
        .then(response => {
          if (response.data.status) {
            this.showSuccess(response.data.message);
            this.fetchTemplates(true);
          } else {
            this.showError(response.data.message);
          }
        })
        .catch(error => {
          this.showError(this.previewErrorMessage(error, 'Failed to duplicate template.'));
        });
    },
    normalizeModalItemSettings() {
      if (!this.modalSettings.item_show || typeof this.modalSettings.item_show !== 'object') {
        this.modalSettings.item_show = { ...DEFAULT_ITEM_SHOW };
      } else {
        Object.keys(DEFAULT_ITEM_SHOW).forEach(k => {
          if (this.modalSettings.item_show[k] === undefined) {
            this.$set(this.modalSettings.item_show, k, true);
          }
        });
      }
      const order = this.modalSettings.item_order;
      const isValidOrder = Array.isArray(order)
        && order.length === DEFAULT_ITEM_ORDER.length
        && DEFAULT_ITEM_ORDER.every(k => order.includes(k));
      if (!isValidOrder) {
        this.$set(this.modalSettings, 'item_order', DEFAULT_ITEM_ORDER.slice());
      }
      const defaults = createDefaultItemStyles();
      if (!this.modalSettings.item_styles || typeof this.modalSettings.item_styles !== 'object') {
        this.$set(this.modalSettings, 'item_styles', defaults);
      } else {
        Object.keys(defaults).forEach(key => {
          const existing = this.modalSettings.item_styles[key];
          if (!existing || typeof existing !== 'object') {
            this.$set(this.modalSettings.item_styles, key, defaults[key]);
          } else {
            if (existing.enabled === undefined) this.$set(existing, 'enabled', false);
            if (existing.font_family === undefined) this.$set(existing, 'font_family', '');
            if (existing.font_size === undefined) this.$set(existing, 'font_size', defaults[key].font_size);
            if (existing.color === undefined) this.$set(existing, 'color', defaults[key].color);
          }
        });
      }
    },
    // Everything a save would write, normalized so it can be compared against
    // itself later. buildComparableSnapshot() covers the design fields; the
    // three added here are the ones it deliberately leaves out because a
    // layout switch does not touch them.
    buildSaveSnapshot(settings) {
      const snapshot = this.buildComparableSnapshot(settings);
      snapshot.template_name = (settings.template_name || '').trim();
      snapshot.template_key = String(settings.template_key || '');
      snapshot.signatory_id = settings.signatory_id === undefined || settings.signatory_id === ''
        ? null
        : settings.signatory_id;
      return JSON.stringify(snapshot);
    },
    openEdit(template) {
      this.editingTemplate = template;
      // Deep clone, not a spread: item_show / item_order / item_styles are
      // nested, and a shallow copy leaves the editor mutating the list row
      // itself — so discarding changes would leave those edits behind in the
      // list until the next fetch.
      this.modalSettings = JSON.parse(JSON.stringify(template));
      this.previousTemplateKey = String(template.template_key);
      this.onSignatoryChange(this.modalSettings.signatory_id);
      this.normalizeModalItemSettings();
      this.showAdvanced = false;
      this.syncAdvancedVisibility(template.template_key);
      this.modalSubmitted = false;
      this.allowModalClose = false;
      this.showModal = true;
      // Taken after normalizeModalItemSettings() so the baseline is in the
      // same shape a later comparison will produce — otherwise merely opening
      // and saving a template would always read as "changed".
      this.openSnapshot = this.buildSaveSnapshot(this.modalSettings);
      this.$nextTick(() => {
        this.editorKey++;
      });
    },
    // Every way out of the editor short of a successful save funnels through
    // here — Cancel, the header ✕ and Esc all end in a hide — so the
    // unsaved-changes guard only has to be written once. Preventing the event
    // leaves the editor open and re-syncs showModal back to true, so the
    // discard prompt is answered with the work still on screen.
    onModalHide(bvModalEvent) {
      if (!this.allowModalClose && this.hasUnsavedChanges()) {
        bvModalEvent.preventDefault();
        this.confirmDiscardChanges();
        return;
      }
      this.allowModalClose = false;
      this.editingTemplate = null;
      this.editorExpanded = false;
      this.modalSubmitted = false;
      this.openSnapshot = null;
    },
    // Compared with buildSaveSnapshot() rather than a raw object diff so this
    // agrees with saveModal()'s "you haven't changed anything" check: the two
    // must never disagree about whether the template was touched.
    hasUnsavedChanges() {
      return this.openSnapshot !== null
        && this.buildSaveSnapshot(this.modalSettings) !== this.openSnapshot;
    },
    confirmDiscardChanges() {
      this.$bvModal.msgBoxConfirm('Your unsaved changes will be lost.', {
        title: 'Are you sure?',
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'primary',
        okTitle: 'Discard Changes',
        cancelVariant: 'secondary',
        cancelTitle: 'Keep Editing',
        footerClass: 'p-2',
        modalClass: 'idcard-confirm-modal',
        hideHeaderClose: false,
        centered: true
      }).then(confirmed => {
        // Dismissing the prompt itself (Esc / ✕) resolves null — same as
        // "Keep Editing", so the editor stays put.
        if (!confirmed) return;
        this.allowModalClose = true;
        this.showModal = false;
      });
    },
    // Builds a plain, normalized snapshot of only the fields that a layout
    // switch would overwrite (colors, fonts, item show/order/styles, etc.),
    // filling in the same defaults normalizeModalItemSettings() would apply.
    // Used to detect whether the current settings still match the layout's
    // stock defaults, so we can skip the "you'll lose your changes" warning
    // when there's nothing to actually lose.
    buildComparableSnapshot(settings) {
      const scalarKeys = [
        'font_style', 'card_orientation', 'image_shape', 'footer_text',
        'primary_color', 'secondary_color', 'name_text_color', 'job_title_color',
        'department_color', 'info_text_color', 'icon_color', 'back_text_color'
      ];
      const snapshot = {};
      scalarKeys.forEach(key => {
        snapshot[key] = settings[key] !== undefined && settings[key] !== null ? settings[key] : null;
      });
      snapshot.custom_html = settings.custom_html || '';

      snapshot.item_show = {
        ...DEFAULT_ITEM_SHOW,
        ...(settings.item_show && typeof settings.item_show === 'object' ? settings.item_show : {})
      };

      const order = settings.item_order;
      const isValidOrder = Array.isArray(order)
        && order.length === DEFAULT_ITEM_ORDER.length
        && DEFAULT_ITEM_ORDER.every(k => order.includes(k));
      snapshot.item_order = isValidOrder ? [...order] : [...DEFAULT_ITEM_ORDER];

      const defaultStyles = createDefaultItemStyles();
      const styles = settings.item_styles && typeof settings.item_styles === 'object' ? settings.item_styles : {};
      snapshot.item_styles = {};
      Object.keys(defaultStyles).forEach(key => {
        const existing = styles[key];
        snapshot.item_styles[key] = existing && typeof existing === 'object'
          ? {
              enabled: existing.enabled !== undefined ? existing.enabled : false,
              // Laravel's ConvertEmptyStringsToNull middleware turns the ""
              // ("Template Default") selection into null on save, so a
              // freshly-normalized "" and a saved-then-reloaded null must
              // compare equal here — otherwise every template that's ever
              // been saved once falsely reads as "customized" forever after.
              font_family: existing.font_family || '',
              font_size: existing.font_size !== undefined ? existing.font_size : defaultStyles[key].font_size,
              color: existing.color !== undefined ? existing.color : defaultStyles[key].color
            }
          : { ...defaultStyles[key] };
      });

      return snapshot;
    },
    // Narrows a comparable snapshot down to just the fields the Advanced
    // Settings panel owns — the advanced colors plus item show/order/styles.
    // Built off buildComparableSnapshot() so both comparisons normalize
    // identically (notably the ""-vs-null font_family quirk).
    buildAdvancedSnapshot(settings) {
      const full = this.buildComparableSnapshot(settings);
      const snapshot = {};
      this.advancedColorFields.forEach(field => {
        snapshot[field.key] = full[field.key];
      });
      ['item_show', 'item_order', 'item_styles'].forEach(key => {
        snapshot[key] = full[key];
      });
      return snapshot;
    },
    // Fetches company_id=0 defaults for a template_key, caching the result
    // so repeated lookups (comparison + apply, or switching back and forth
    // between the same layouts) cost at most one request per key per
    // session instead of one per attempt.
    getLayoutDefaults(key) {
      const cacheKey = String(key);
      if (this.layoutDefaultsCache[cacheKey]) {
        // Deep-clone on every read — callers (e.g. applyTemplateDesign)
        // mutate nested item_show/item_order/item_styles in place via
        // normalizeModalItemSettings(), which would otherwise corrupt the
        // cached copy for later reads.
        return Promise.resolve(JSON.parse(JSON.stringify(this.layoutDefaultsCache[cacheKey])));
      }
      return axios.get('/id-card/default-by-key/' + key).then(response => {
        this.layoutDefaultsCache[cacheKey] = response.data;
        return JSON.parse(JSON.stringify(response.data));
      });
    },
    // True when the template being edited carries advanced customizations —
    // i.e. anything under Advanced Settings differs from the layout's stock
    // defaults. Fails closed (treats as "untouched") on a fetch error: the
    // panel just stays collapsed, which is the current behaviour anyway.
    hasAdvancedCustomizations(key) {
      return this.getLayoutDefaults(key)
        .then(defaults => {
          const currentSnapshot = this.buildAdvancedSnapshot(this.modalSettings);
          const defaultSnapshot = this.buildAdvancedSnapshot(defaults);
          return JSON.stringify(currentSnapshot) !== JSON.stringify(defaultSnapshot);
        })
        .catch(() => false);
    },
    // Advanced Settings opens collapsed by default, but once a template has
    // advanced customizations saved on it we expand the panel on open so the
    // user lands straight on what they changed last time.
    syncAdvancedVisibility(key) {
      const templateAtOpen = this.editingTemplate;
      this.hasAdvancedCustomizations(key).then(hasCustomizations => {
        if (!hasCustomizations) return;
        // Drop the result if the modal moved on while the defaults were in
        // flight — closed, switched to another template, or switched layout
        // (applyTemplateDesign deliberately collapses the panel).
        if (this.editingTemplate !== templateAtOpen) return;
        if (String(this.modalSettings.template_key) !== String(key)) return;
        this.showAdvanced = true;
      });
    },
    // Compares the modal's current settings against the (possibly cached)
    // defaults for currentKey. Fails open (treats as "customized") on any
    // fetch error so the warning still shows rather than silently
    // discarding real changes.
    hasLayoutCustomizations(currentKey) {
      return this.getLayoutDefaults(currentKey)
        .then(defaults => {
          const currentSnapshot = this.buildComparableSnapshot(this.modalSettings);
          const defaultSnapshot = this.buildComparableSnapshot(defaults);
          return JSON.stringify(currentSnapshot) !== JSON.stringify(defaultSnapshot);
        })
        .catch(() => true);
    },
    onTemplateDesignChange(newKey) {
      const previous = this.previousTemplateKey;
      if (String(newKey) === String(previous)) return;

      this.modalSaving = true;
      this.hasLayoutCustomizations(previous).then(hasCustomizations => {
        if (!hasCustomizations) {
          this.applyTemplateDesign(newKey);
          return;
        }
        this.modalSaving = false;
        this.$bvModal.msgBoxConfirm(
          [
            this.$createElement('p', { class: 'mb-3' },
              "Switching the layout will replace all current design settings below with that layout's defaults."),
            this.$createElement('div', {
              class: 'p-2 rounded',
              style: 'background:#fff8e1;border-left:3px solid #ffc107;'
            }, [
              this.$createElement('strong', 'Recommendation: '),
              "Save this template as it is, then duplicate it to try a new layout — that way your current customization is preserved."
            ])
          ],
          {
            title: 'Change Template Layout',
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'primary',
            okTitle: 'Switch Layout',
            cancelVariant: 'secondary',
            cancelTitle: 'Cancel',
            footerClass: 'p-2',
            modalClass: 'idcard-confirm-modal',
            hideHeaderClose: false,
            centered: true
          }
        ).then(confirmed => {
          if (!confirmed) {
            this.modalSettings.template_key = previous;
            return;
          }
          this.applyTemplateDesign(newKey);
        });
      });
    },
    applyTemplateDesign(key) {
      this.modalSaving = true;
      this.getLayoutDefaults(key)
        .then(defaults => {
          const preserved = {
            id: this.modalSettings.id,
            company_id: this.modalSettings.company_id,
            template_name: this.modalSettings.template_name,
            signatory_id: this.modalSettings.signatory_id,
            is_active: this.modalSettings.is_active,
            is_default: this.modalSettings.is_default
          };
          this.modalSettings = {
            ...defaults,
            ...preserved,
            template_key: String(key)
          };
          this.previousTemplateKey = String(key);
          this.normalizeModalItemSettings();
          this.showAdvanced = false;
          this.$nextTick(() => {
            this.editorKey++;
          });
        })
        .catch(() => {
          this.showError('Failed to load the selected template layout.');
          this.modalSettings.template_key = this.previousTemplateKey;
        })
        .finally(() => {
          this.modalSaving = false;
        });
    },
    colorFieldState(key) {
      if (!this.modalSubmitted) return null;
      return !!(this.modalSettings[key] && this.modalSettings[key].trim().length);
    },
    isModalValid() {
      return this.nameState !== false
        && this.signatoryState !== false
        && this.fontStyleState !== false
        && this.orientationState !== false
        && this.imageShapeState !== false
        && this.footerTextState !== false
        && this.primaryColorFields.every(field => this.colorFieldState(field.key) !== false);
    },
    toggleEditorHeight() {
      this.editorExpanded = !this.editorExpanded;
    },
    onEditorReady(editor) {
      // Editor configuration if needed
    },
    saveModal() {
      this.modalSubmitted = true;
      if (!this.isModalValid()) {
        this.showError("Please fill in all required fields.");
        return;
      }

      // Saving a template nobody touched still writes a row and still files an
      // activity-log entry, so say so rather than reporting a change that was
      // never made.
      if (this.openSnapshot !== null && this.buildSaveSnapshot(this.modalSettings) === this.openSnapshot) {
        this.$bvModal.msgBoxConfirm(
          "You haven't changed anything in this template. Save it anyway?",
          {
            title: 'No Changes Made',
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'primary',
            okTitle: 'Save Anyway',
            cancelVariant: 'secondary',
            cancelTitle: 'Keep Editing',
            footerClass: 'p-2',
            modalClass: 'idcard-confirm-modal',
            hideHeaderClose: false,
            centered: true
          }
        ).then(confirmed => {
          if (confirmed) this.submitModal();
        });
        return;
      }

      this.submitModal();
    },
    submitModal() {
      this.modalSaving = true;
      axios.post('/id-card/update', {
        id: this.editingTemplate.id,
        ...this.modalSettings
      })
      .then(response => {
        if (response.data.status) {
          this.showSuccess(response.data.message);
          // The changes are on the server now, so the guard must not ask
          // about discarding them on the way out.
          this.allowModalClose = true;
          this.showModal = false;
          this.fetchTemplates();
        } else {
          this.showError(response.data.message);
        }
      })
      .catch(error => {
        this.showError("Failed to update template.");
      })
      .finally(() => {
        this.modalSaving = false;
      });
    },
    previewTemplate() {
      if (this.previewLoading) return;
      this.previewLoading = true;
      axios.post('/id-card/live-preview-html', { ...this.modalSettings })
        .then(response => {
          this.previewFaces = response.data.faces || [];
          this.showPreviewModal = true;
        })
        .catch(error => {
          this.showError(this.previewErrorMessage(error, 'Failed to generate preview.'));
        })
        .finally(() => {
          this.previewLoading = false;
        });
    },
    onPreviewModalHidden() {
      this.previewFaces = [];
    },
    previewSavedTemplate(template) {
      if (this.previewRowLoadingId) return;
      this.previewRowLoadingId = template.id;
      axios.post('/id-card/live-preview-html', { ...template })
        .then(response => {
          this.savedPreviewFaces = response.data.faces || [];
          this.showSavedPreviewModal = true;
        })
        .catch(error => {
          this.showError(this.previewErrorMessage(error, 'Failed to generate preview.'));
        })
        .finally(() => {
          this.previewRowLoadingId = null;
        });
    },
    previewErrorMessage(error, fallback) {
      const data = error.response && error.response.data;
      return (data && (data.error || data.message)) || fallback;
    },
    // Downloads the same PDF the preview shows, without opening the modal.
    downloadTemplate(template) {
      if (this.downloadRowLoadingId) return;
      this.downloadRowLoadingId = template.id;
      axios.post('/id-card/live-preview', { ...template }, { responseType: 'blob' })
        .then(response => {
          const blob = new Blob([response.data], { type: 'application/pdf' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = this.previewFileName(template);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        })
        .catch(async error => {
          let message = 'Failed to download template.';
          if (error.response && error.response.data instanceof Blob) {
            try {
              const text = await error.response.data.text();
              message = JSON.parse(text).error || message;
            } catch (e) { /* keep default message */ }
          }
          this.showError(message);
        })
        .finally(() => {
          this.downloadRowLoadingId = null;
        });
    },
    previewFileName(template) {
      const name = (template && template.template_name) || 'ID_Card_Template';
      return name.trim().replace(/[^\w\-\s]/g, '').replace(/\s+/g, '_') + '.pdf';
    },
    onSavedPreviewModalHidden() {
      this.savedPreviewFaces = [];
    },
    openSignatoryModal(template) {
      this.signatoryModalTemplate = template;
      this.signatoryModalValue = template.signatory_id;
      this.signatoryModalSubmitted = false;
      this.onSignatoryModalChange(this.signatoryModalValue);
      this.showSignatoryModal = true;
    },
    onSignatoryModalChange(sigId) {
      const found = this.signatories.find(s => s.id == sigId);
      this.signatoryModalPreview = found && found.signature ? this.signatureUrl(found.id) : null;
    },
    saveSignatory() {
      // The editor treats Authorized Signature as required, so this shortcut
      // must not be a side door for clearing it.
      if (this.signatoryModalValue === null || this.signatoryModalValue === undefined || this.signatoryModalValue === '') {
        this.signatoryModalSubmitted = true;
        this.showError('Please select a signatory.');
        return;
      }
      this.signatoryModalSaving = true;
      axios.post('/id-card/update-signatory', {
        id: this.signatoryModalTemplate.id,
        signatory_id: this.signatoryModalValue
      })
        .then(response => {
          if (response.data.status) {
            this.showSuccess(response.data.message);
            this.showSignatoryModal = false;
            this.fetchTemplates();
          } else {
            this.showError(response.data.message);
          }
        })
        .catch(() => {
          this.showError('Failed to update signatory.');
        })
        .finally(() => {
          this.signatoryModalSaving = false;
        });
    },
    deleteTemplate(id) {
      this.$bvModal.msgBoxConfirm('Are you sure you want to delete this template?', {
        title: 'Please Confirm',
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'primary',
        okTitle: 'Delete',
        cancelVariant: 'secondary',
        cancelTitle: 'Cancel',
        footerClass: 'p-2',
        modalClass: 'idcard-confirm-modal',
        hideHeaderClose: false,
        centered: true
      }).then(confirmed => {
        if (!confirmed) return;
        this.loading = true;
        axios.post('/id-card/delete', { id })
          .then(response => {
            if (response.data.status) {
              this.showSuccess(response.data.message);
              this.fetchTemplates();
            } else {
              this.showError(response.data.message);
            }
          })
          // The server refuses to delete a default or active template with a
          // 403, which never reaches the handler above — without this the
          // refusal was swallowed and the row just stayed put unexplained.
          .catch(error => {
            this.showError(this.previewErrorMessage(error, 'Failed to delete template.'));
          })
          .finally(() => { this.loading = false; });
      });
    },
    showSuccess(msg) {
      this.$bvToast.toast(msg, { title: 'Success', variant: 'success', solid: true });
    },
    showError(msg) {
      this.$bvToast.toast(msg, { title: 'Error', variant: 'danger', solid: true });
    }
  }
}
</script>

<style scoped>
.template-wrapper {
  background: #fff;
  box-shadow: 0 0 10px rgba(0,0,0,0.05);
}
.action-dropdown-toggle::after {
  display: none;
}
/* Fullscreen Modal Override */
.idcard-modal-fullscreen {
  max-width: 100% !important;
  width: 100% !important;
  height: 100% !important;
  margin: 0 !important;
}
</style>

<style>
/* Global style for modal because it's teleported */
.idcard-modal-fullscreen .modal-content {
  height: 100vh !important;
  border-radius: 0 !important;
}

/* Global (not scoped) so it also applies inside the FieldStyleBox child component */
.color-picker-input {
  width: 36px;
  height: 32px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  cursor: pointer;
  padding: 2px;
}

/* Color Settings / Advance Color pickers, matching the Edit Leave Category
   control (app/Modules/Roster/Components/LeaveTypes.vue): the field name and
   its hex on the left, a full-height colour block on the right. */
.idcard-color-box {
  height: 80px;
  padding-right: 10px;
  overflow: hidden;
  border-radius: 16px !important;
  margin-right: 2px;
}
.idcard-color-swatch.form-control {
  height: 60px;
  cursor: pointer;
  border-radius: 10px;
}
.monospace-font {
  font-family: monospace;
}

/* Confirm-dialog footer buttons.
   The theme paints .btn-secondary exactly like .btn-primary, so the two are
   told apart here by swapping the theme's own solid/light states: Cancel sits
   in the light state and goes solid on hover, Delete does the reverse. */
.idcard-confirm-modal .modal-footer .btn-secondary,
.idcard-confirm-modal .modal-footer .btn-primary:hover,
.idcard-confirm-modal .modal-footer .btn-primary:focus,
.idcard-confirm-modal .modal-footer .btn-primary:not(:disabled):not(.disabled):active {
  background-color: #DDF5F9 !important;
  border-color: #0892A4 !important;
  color: #004242 !important;
}

.idcard-confirm-modal .modal-footer .btn-primary,
.idcard-confirm-modal .modal-footer .btn-secondary:hover,
.idcard-confirm-modal .modal-footer .btn-secondary:focus,
.idcard-confirm-modal .modal-footer .btn-secondary:not(:disabled):not(.disabled):active {
  background-color: #004242 !important;
  border-color: #004242 !important;
  color: #fff !important;
}

.idcard-confirm-modal .modal-footer .btn:focus {
  box-shadow: 0 0 0 0.2rem rgba(8, 146, 164, 0.35) !important;
}

/* Editor footer: Cancel button sits in light state normally, goes solid dark teal on hover */
body .idcard-editor-footer .idcard-cancel-btn,
body .idcard-editor-footer .idcard-cancel-btn:not(:disabled):not(.disabled),
body .modal-footer .idcard-cancel-btn,
body .modal-footer .idcard-cancel-btn:not(:disabled):not(.disabled) {
  background-color: #DDF5F9 !important;
  border-color: #0892A4 !important;
  color: #004242 !important;
}

body .idcard-editor-footer .idcard-cancel-btn:hover,
body .idcard-editor-footer .idcard-cancel-btn:focus,
body .idcard-editor-footer .idcard-cancel-btn:active,
body .idcard-editor-footer .idcard-cancel-btn:not(:disabled):not(.disabled):hover,
body .idcard-editor-footer .idcard-cancel-btn:not(:disabled):not(.disabled):focus,
body .modal-footer .idcard-cancel-btn:hover,
body .modal-footer .idcard-cancel-btn:focus,
body .modal-footer .idcard-cancel-btn:active,
body .modal-footer .idcard-cancel-btn:not(:disabled):not(.disabled):hover,
body .modal-footer .idcard-cancel-btn:not(:disabled):not(.disabled):focus {
  background-color: #004242 !important;
  border-color: #004242 !important;
  color: #ffffff !important;
}

/* Update Template button: sits in solid dark teal, goes light on hover */
body .idcard-editor-footer .idcard-update-btn,
body .idcard-editor-footer .idcard-update-btn:not(:disabled):not(.disabled),
body .modal-footer .idcard-update-btn,
body .modal-footer .idcard-update-btn:not(:disabled):not(.disabled) {
  background-color: #004242 !important;
  border-color: #004242 !important;
  color: #ffffff !important;
}

body .idcard-editor-footer .idcard-update-btn:hover,
body .idcard-editor-footer .idcard-update-btn:focus,
body .idcard-editor-footer .idcard-update-btn:active,
body .idcard-editor-footer .idcard-update-btn:not(:disabled):not(.disabled):hover,
body .idcard-editor-footer .idcard-update-btn:not(:disabled):not(.disabled):focus,
body .modal-footer .idcard-update-btn:hover,
body .modal-footer .idcard-update-btn:focus,
body .modal-footer .idcard-update-btn:active,
body .modal-footer .idcard-update-btn:not(:disabled):not(.disabled):hover,
body .modal-footer .idcard-update-btn:not(:disabled):not(.disabled):focus {
  background-color: #DDF5F9 !important;
  border-color: #0892A4 !important;
  color: #004242 !important;
}

.idcard-editor-footer .btn:focus {
  box-shadow: 0 0 0 0.2rem rgba(8, 146, 164, 0.35) !important;
}
</style>
